import React, { useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import { formatErrorFieldMessages } from 'interfaces'
import {
  ButtonWithIcon,
  EditActions,
  detailsContainerStyles,
  IconButtonWithTooltip,
  LastUpdatedComponent,
  useGlobalMessageHandler
  , accordionSummaryStyles, cardStyles, CommitteeBasicInfoFields, AlertDialog, CommentDialog, ResponsibilityMultiSelect
} from 'components'
import { useSessionContext } from 'session'
import { type CommitteeBasicInformation, deleteCommittee, updateComBasicInfo } from 'services'
import DeleteIcon from '@mui/icons-material/Delete'
import { useNavigate } from 'react-router-dom'

interface CommitteeEditProps {
  originalBasicInfo: CommitteeBasicInformation
  loading: boolean
  updateBasicInfo: React.Dispatch<React.SetStateAction<CommitteeBasicInformation | undefined>>
}

export const CommitteeBasicInfoCard: React.FC<CommitteeEditProps> = ({ originalBasicInfo, loading, updateBasicInfo }) => {
  const [expanded, setExpanded] = useState(true)
  const [committeeBasicInfo, setCommitteeBasicInfo] = useState<CommitteeBasicInformation>(originalBasicInfo)
  const [disabled, setDisabled] = useState<boolean>(true)
  const [loadingClick, setLoadingClick] = useState(false)
  const [errorFields, setErrorFields] = useState<string[]>([])
  const { openNewTab } = useSessionContext()
  const navigate = useNavigate()
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)

  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()
  const handleAccordionChange = (): void => {
    setExpanded(!expanded)
  }

  const toggleDisable = (): void => {
    setDisabled(prevState => {
      // Initialize responsibilities if not present when starting to edit
      if (prevState && (committeeBasicInfo.responsibilities == null)) {
        setCommitteeBasicInfo(prev => ({ ...prev, responsibilities: [] }))
      }
      return !prevState
    })
  }
  const toggleLoadingClick = (): void => {
    setLoadingClick(prevState => !prevState)
  }

  const handleDeleteClick = async (): Promise<void> => {
    try {
      setLoadingClick(true)
      const response = await deleteCommittee(originalBasicInfo.id)
      if (response.succeeded && response.result != null) {
        showSuccessMessage('Committee deleted')
        navigate('/us/committee')
      }
    } catch (error: any) {
      showErrorMessage(formatErrorFieldMessages(error.response.data.errors))
    } finally {
      setLoadingClick(false)
    }
  }

  const handlePostClick = (): void => {
    clearMessages()
    toggleLoadingClick()
    const { addresses, ...payload } = committeeBasicInfo
    void updateComBasicInfo(originalBasicInfo.id, payload).then(value => {
      if (value.succeeded) {
        showSuccessMessage('This committee has been successfully updated!\n')
        updateBasicInfo(committeeBasicInfo)
        toggleDisable()
      } else {
        showErrorMessage(formatErrorFieldMessages(value.errors))
        if (value.errors != null) {
          setErrorFields(Object.entries(value.errors).map(([key, value]) => key))
        }
      }
    }).catch(() => {
      showErrorMessage('Failed to update Committee')
    }).finally(() => {
      toggleLoadingClick()
    })
  }

  const handleCancel = (): void => {
    clearMessages()
    setCommitteeBasicInfo(originalBasicInfo)
    setErrorFields([])
    setDisabled(true)
  }

  return (
    <Card sx={cardStyles}>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header"
                          sx={accordionSummaryStyles}>
          <div>
            <Typography variant="h6">Basic Information</Typography>
            <LastUpdatedComponent value={committeeBasicInfo?.lastUpdated}></LastUpdatedComponent>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={1.5} md={1.2}>
                <Box display="flex" flexDirection="column">
                  <Box mt={4}>
                    <Stack spacing={3}>
                    <ButtonWithIcon fullWidth={true} nameButton={'Members'} icon={<PeopleOutlineIcon />} onClick={function (): void {
                      openNewTab(`/us/committee/members/${committeeBasicInfo.id}`)
                    } } />
                    <ButtonWithIcon fullWidth={true} nameButton={'Staffers'} icon={<PeopleOutlineIcon />} onClick={function (): void {
                      openNewTab(`/us/committee/staffers/${committeeBasicInfo.id}`)
                    } } />
                    </Stack>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={10.5} sx={detailsContainerStyles}>
                <Stack spacing={1}>
                  <CommitteeBasicInfoFields loading={loadingClick} committee={committeeBasicInfo} updateValue={setCommitteeBasicInfo}
                                            isFieldEmpty={(field: string) => committeeBasicInfo[field as keyof CommitteeBasicInformation] == null || committeeBasicInfo[field as keyof CommitteeBasicInformation] === '' }
                                            disabled={disabled} errorFields={errorFields}/>
                  <ResponsibilityMultiSelect
                    disabled={disabled}
                    value={committeeBasicInfo.responsibilities ?? []}
                    onChange={(selectedIds) => {
                      setCommitteeBasicInfo(prev => ({ ...prev, responsibilities: selectedIds }))
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'end' }}>
              {loadingClick && <CircularProgress/>}
              <EditActions
                entityName='Committee'
                editing={!disabled}
                saving={loadingClick}
                disabled={loading}
                onEdit={toggleDisable}
                onCancel={handleCancel}
                onSave={handlePostClick}
              />
              <CommentDialog recordId={committeeBasicInfo.id} recordType={'C'} recordStateId={committeeBasicInfo.state.id}/>
              <IconButtonWithTooltip label={'Delete Committee'} color="secondary" disabled={loadingClick || loading} icon={<DeleteIcon />} onClick={() => { setOpenDeleteDialog(true) }}/>
              <AlertDialog
                  onClose={() => { setOpenDeleteDialog(false) }}
                  open={openDeleteDialog}
                  onButtonClick={() => { void handleDeleteClick() }}
                  title='Would you like to delete this Committee?'
                  contentText='Deleting a committee will recursively remove all associated sub-committees, members, staffers, and their addresses, phones, and URLs. These changes cannot be undone'
                  nameButtonCancel='Close'
                  nameButtonConfirm='Delete'
                  containedButton = {true}
              />
            </Box>
          </CardContent>
        </AccordionDetails>
      </Accordion>
    </Card>
  )
}
